/**
 * Favicon resolution for cited sources.
 *
 * Cloud builds resolve a favicon through Google's favicon service, which means
 * every rendered citation tells a third party which domain was cited. An
 * airgapped on-prem appliance must make no such request, so the remote lookup
 * is a switch the APP sets once at bootstrap (`src/composition/wiring/favicon-wiring.ts`) —
 * this package never reads the build profile (F5: env is injected, never read).
 *
 * With the remote lookup off, nothing here returns a network URL:
 * `getFaviconUrl` answers with a bundled generic icon as a `data:` URI (allowed
 * by the on-prem CSP's `img-src 'self' data: blob:`), `getDomainFaviconUrl`
 * answers `undefined` so its consumers take their own no-icon branch, and
 * `prefetchFavicons` does nothing.
 */

export interface FaviconConfig {
  /**
   * Whether favicons may be fetched from the third-party favicon service.
   * `false` on a build that must not egress.
   */
  remoteLookupEnabled: boolean;
}

// Defaults OPEN so a jest suite, a story or the widget that never runs the app
// wiring keeps today's behaviour. The on-prem build closes it in
// `favicon-wiring.ts`, and the on-prem CSP blocks the host regardless.
let remoteLookupEnabled = true;

/** One-time app-bootstrap injection seam. */
export function configureFavicons(config: FaviconConfig): void {
  remoteLookupEnabled = config.remoteLookupEnabled;
}

/**
 * Whether a third-party favicon/logo lookup may be made. For the consumers
 * whose request is not a single favicon URL — a fallback chain through a
 * proxy, say — and so cannot route through the resolvers below.
 */
export const isRemoteFaviconLookupEnabled = (): boolean => remoteLookupEnabled;

/**
 * A neutral globe, inlined so it needs no request and no asset pipeline. The
 * stroke is a fixed mid-grey because an `<img>` cannot inherit `currentColor`;
 * it reads on both the light and the dark surface.
 */
export const GENERIC_FAVICON_DATA_URI =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
      'stroke="#8c8c8c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="10"/>' +
      '<path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>' +
      '<path d="M2 12h20"/></svg>'
  );

/**
 * Favicon `src` for a source URL: Google's favicon API when the remote lookup
 * is enabled, the bundled generic icon otherwise. Always a usable `src`.
 */
export const getFaviconUrl = (url: string): string => {
  if (!remoteLookupEnabled) return GENERIC_FAVICON_DATA_URI;
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?sz=64&domain_url=${domain}`;
  } catch {
    return `https://www.google.com/s2/favicons?sz=64&domain_url=${url}`;
  }
};

/**
 * Favicon URL for a bare DOMAIN, for consumers that carry an optional
 * `iconUrl` and already render a fallback without one (the source viewer
 * header). `undefined` when the remote lookup is disabled.
 */
export const getDomainFaviconUrl = (domain: string, size = 32): string | undefined => {
  if (!remoteLookupEnabled) return undefined;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;
};

/**
 * Prefetch favicons using native browser Image API
 * Browser automatically caches after first fetch. A no-op when the remote
 * lookup is disabled — there is nothing to warm, and the point is not to ask.
 */
export const prefetchFavicons = (urls: string[]): void => {
  if (!remoteLookupEnabled) return;
  const uniqueUrls = [...new Set(urls)];
  uniqueUrls.forEach((url) => {
    new Image().src = getFaviconUrl(url);
  });
};
